import { useSupportSheet } from "../../components/SupportSheet";
import {
  formatBankAccountLabel,
  looksLikePayerCode,
} from "../../utils/bankAccountLabel";
import { memo, useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BodyCopy } from "../../components/BodyCopy";
import {
  BankIcon,
  Button,
  CardIcon,
  CashSpecialIcon,
  ExchangeIcon,
  Layout,
  SupportIcon,
  Title,
  WalletIcon,
  AuraLayout,
  WarningIcon,
} from "lola-framework-ui-test";
import { FlagCircle } from "../../components/FlagCircle";
import { QuoteInfo } from "../../components/send/QuoteInfo";
import { PayoutInfo } from "../../components/PayoutInfo";
import { FooterDetailSummary } from "../../components/FooterDetailSummary";
import { Popup } from "../../components/Popup";
import {
  SelectPayoutMethodSlot,
  type FlatPayoutPick,
} from "../../components/send/SelectPayoutMethodSlot";
import {
  PayoutDetailsSlot,
  type PayoutDetailsResult,
} from "../../components/send/PayoutDetailsSlot";
import {
  SelectFundingMethodSlot,
  type FlatFundingPick,
} from "../../components/send/SelectFundingMethodSlot";
import { AmountOutOfRangeInfo } from "../../components/AmountOutOfRangeInfo";
import { RecipientDetailsSlot } from "../../components/send/RecipientDetailsSlot";
import { SelectDestinationCountrySlot } from "../../components/send/SelectDestinationCountrySlot";
import { SelectOriginCountrySlot } from "../../components/send/SelectOriginCountrySlot";
import { SEND_ROUTES, TITLE_COLOR } from "../../constants";
import {
  buildSendPreQuote,
  sendCountries,
  sendOriginCountries,
  sendSavedCards,
  sendFundingDetails,
} from "../../gallery/sendMock";
import { buildMockQuote } from "../../gallery/mockData";
import {
  applyFirstSendSeed,
  isFirstSendWarningVisible,
} from "../../gallery/firstSendMock";
import { FirstSendWarning } from "../../components/first-send/FirstSendWarning";
import { useGalleryFirstSend } from "../../hooks/useGalleryFirstSend";
import { useGalleryPickerLoading } from "../../hooks/useGalleryPickerLoading";
import { useGalleryStore } from "../../gallery/galleryStore";
import { useStore } from "../../store/globalState";
import { useSessionStore } from "../../store/useSessionStore";
import { useSendStore } from "../../store/useSendStore";
import { useComposeStore } from "../../store/useComposeStore";
import { PayoutMethodType } from "../../interfaces/payout";
import {
  FUNDING_METHOD_CATEGORY,
  availableFundingMethods,
  optionForFundingMethod,
  type FundingMethod,
} from "../../utils/sendCatalog";
import { currencyForCountry } from "../../utils/catalogCountries";
import {
  seedDestinationCountry,
  seedOriginCountry,
  supportedDestinations,
  supportedOrigins,
} from "../../utils/sendCorridors";
import type { EstimateOption } from "../../interfaces/payout";
import { amountFormatter } from "../../utils";
import { compareSendToBound } from "../../utils/fundingLimits";
import {
  compatibleBookDestinations,
  type CompatibleBookDestination,
} from "../../utils/recipient-destinations";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import masterCardLogo from "../../assets/master_card_icon.svg";
import visaLogo from "../../assets/visa_color_icon.svg";
import { Navbar } from "../../components/Navbar";

const SUPPORTED_PAYOUT_METHODS: PayoutMethodType[] = [
  PayoutMethodType.BANK_ACCOUNT,
  PayoutMethodType.CASH_PICKUP,
  PayoutMethodType.PUSH_TO_CARD,
  PayoutMethodType.MOBILE_WALLET,
];

const isSupportedPayoutMethod = (value: string): value is PayoutMethodType =>
  (SUPPORTED_PAYOUT_METHODS as string[]).includes(value);

type PayoutStep = "method" | "details";
type ComposeStep = "funding" | "payout" | "recipient";

const COMPOSE_STEP_ORDER: ComposeStep[] = ["funding", "recipient", "payout"];

/**
 * `primaryMesh` is usually a gradient string, which is invalid for `color` and
 * `border-color`. The outlined icon in the rate pill needs a solid value, so it
 * falls back to the gradient's first stop.
 */
const solidStop = (color: string) =>
  color.match(/#[0-9a-f]{3,8}|rgba?\([^)]*\)/i)?.[0] ?? color;

export const TransferPage = memo(() => {
  const { t } = useTranslation("send");
  const { theme, amountState } = useStore();
  const { session } = useSessionStore();
  const { setDraft, setQuoteState } = useSendStore();
  const firstSend = useGalleryFirstSend();
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("send");
  const savedListsUi = useGalleryStore((s) => s.savedListsUi);

  const { amount, anchor: amountAnchor = "send" } = amountState;
  const colorConfig = theme.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    dropzoneColors,
    bannerHighlightBackground,
    bannerHighlightColor,
    bannerHighlightIconColors,
  } = colorConfig as typeof colorConfig & {
    bannerHighlightBackground?: string;
    bannerHighlightColor?: string;
    bannerHighlightIconColors?: string[];
  };

  const config = session.data?.config;
  const corridors = config?.corridors ?? [];
  const initialCorridor = config?.defaultCorridor;

  const {
    originCountry: storedOrigin,
    setOriginCountry,
    destinationCountry: storedDestination,
    setDestinationCountry,
    funding,
    setFunding,
    fundingInstrument,
    setFundingInstrument,
    selection,
    setSelection,
    recipient,
    setRecipient,
    bookDestinations,
    setBookDestinations,
  } = useComposeStore();

  const seededOrigin =
    seedOriginCountry(corridors, initialCorridor) ??
    supportedOrigins(corridors)[0] ??
    "US";
  const seededDestination =
    seedDestinationCountry(corridors, initialCorridor) ??
    supportedDestinations(corridors, seededOrigin)[0] ??
    "MX";
  const originCountry = storedOrigin ?? seededOrigin;
  const destinationCountry = storedDestination ?? seededDestination;

  useEffect(() => {
    if (storedOrigin == null) setOriginCountry(seededOrigin);
  }, [storedOrigin, seededOrigin, setOriginCountry]);

  useEffect(() => {
    if (storedDestination == null) setDestinationCountry(seededDestination);
  }, [storedDestination, seededDestination, setDestinationCountry]);

  const corridorOriginCodes = useMemo(() => {
    const fromCorridors = supportedOrigins(corridors);
    if (fromCorridors.length > 0) return fromCorridors;
    return [seededOrigin];
  }, [corridors, seededOrigin]);

  const originCountriesForPicker = useMemo(
    () =>
      sendOriginCountries.filter((c) =>
        corridorOriginCodes.includes(c.countryCode)
      ),
    [corridorOriginCodes]
  );

  const corridorDestinations = useMemo(() => {
    const fromCorridors = supportedDestinations(corridors, originCountry);
    if (fromCorridors.length > 0) return fromCorridors;
    return [seededDestination];
  }, [corridors, originCountry, seededDestination]);

  const destinationCountriesForPicker = useMemo(
    () =>
      sendCountries.filter((c) =>
        corridorDestinations.includes(c.countryCode)
      ),
    [corridorDestinations]
  );

  const baseSourceCurrency = useMemo(() => {
    if (
      originCountry === config?.defaultCorridor?.originCountryCode &&
      config?.sourceCurrency
    ) {
      return config.sourceCurrency;
    }
    const fromPicker = originCountriesForPicker.find(
      (c) => c.countryCode === originCountry
    );
    return currencyForCountry(fromPicker) ?? "USD";
  }, [
    originCountry,
    config?.defaultCorridor?.originCountryCode,
    config?.sourceCurrency,
    originCountriesForPicker,
  ]);

  const destinationCurrency = useMemo(() => {
    const match = sendCountries.find((c) => c.countryCode === destinationCountry);
    const fromCatalog = currencyForCountry(match);
    if (fromCatalog) return fromCatalog;
    if (
      config?.destinationCurrency &&
      destinationCountry === seededDestination
    ) {
      return config.destinationCurrency;
    }
    return "USD";
  }, [destinationCountry, config?.destinationCurrency, seededDestination]);

  const [popup, setPopup] = useState<
    | "origin"
    | "funding"
    | "payout"
    | "recipient"
    | "destination"
    | "amountLimit"
    | null
  >(null);
  const fundingPickerLoading = useGalleryPickerLoading(popup === "funding");
  const payoutPickerLoading = useGalleryPickerLoading(popup === "payout");
  const [payoutStep, setPayoutStep] = useState<PayoutStep>("method");
  const [pendingMethod, setPendingMethod] = useState<PayoutMethodType | null>(
    null
  );
  // Stand-in for the catalog fetch the live chips wait on, so the saved-list
  // shimmer is visible when the cash details step opens.
  const habitsLoadDelay = useGalleryPickerLoading(
    savedListsUi !== "empty" &&
      popup === "payout" &&
      payoutStep === "details" &&
      pendingMethod === PayoutMethodType.CASH_PICKUP
  );
  // Stand-in for the recipient-book GET the live flow waits on.
  const recentLoadDelay = useGalleryPickerLoading(
    savedListsUi !== "empty" && popup === "recipient"
  );
  const [shake, setShake] = useState(false);
  const [showExcratePopup, setShowExcratePopup] = useState(false);
  const [preQuoteLoading, setPreQuoteLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const handleOriginChange = useCallback(
    (code: string) => {
      if (code !== originCountry) {
        setFunding(null);
        setSelection(null);
        setFundingInstrument(null);
        setPayoutStep("method");
        setPendingMethod(null);

        const validDestinations = supportedDestinations(corridors, code);
        if (
          validDestinations.length > 0 &&
          !validDestinations.includes(destinationCountry)
        ) {
          setDestinationCountry(validDestinations[0]);
        }
      }
      setOriginCountry(code);
    },
    [
      originCountry,
      destinationCountry,
      corridors,
      setFunding,
      setSelection,
      setFundingInstrument,
      setDestinationCountry,
      setOriginCountry,
    ]
  );

  const handleDestinationChange = useCallback(
    (code: string) => {
      if (code !== destinationCountry) {
        setFunding(null);
        setSelection(null);
        setPayoutStep("method");
        setPendingMethod(null);
      }
      setDestinationCountry(code);
    },
    [destinationCountry, setFunding, setSelection, setDestinationCountry]
  );

  const pinFunding = useCallback(
    (m: FundingMethod) => {
      if (m !== funding) {
        setSelection(null);
        setPayoutStep("method");
        setPendingMethod(null);
      }
      setFunding(m);
    },
    [funding, setFunding, setSelection]
  );

  const handleFundingPick = useCallback(
    (pick: FlatFundingPick) => {
      if (pick.kind === "add") {
        pinFunding(pick.method);
        setFundingInstrument(null);
        setPopup(null);
        navigate(SEND_ROUTES.ADD_CARD);
        return;
      }
      if (pick.kind === "cash") {
        pinFunding("CASH_DEPOSIT");
        setFundingInstrument(null);
        setPopup(null);
        return;
      }
      if (pick.kind === "method") {
        pinFunding(pick.method);
        setFundingInstrument(null);
        setPopup(null);
        return;
      }
      if (pick.kind === "account") {
        pinFunding("ACH");
        setFundingInstrument(null);
        setPopup(null);
        return;
      }
      pinFunding(pick.card.fundingMethod ?? "DEBIT_CARD");
      setFundingInstrument({
        paymentMethodId: pick.card.paymentMethodId,
        brand: pick.card.brand,
        last4: pick.card.last4,
      });
      setPopup(null);
    },
    [pinFunding, navigate, setFundingInstrument]
  );

  useEffect(() => {
    if (!firstSend) return;
    const card = sendSavedCards[0];
    return applyFirstSendSeed({
      compose: {
        funding: card.fundingMethod ?? "DEBIT_CARD",
        instrument: {
          paymentMethodId: card.paymentMethodId,
          brand: card.brand,
          last4: card.last4,
        },
      },
    });
  }, [firstSend]);

  const numericAmount = amount ? parseFloat(amount) : NaN;
  const hasAmount = !isNaN(numericAmount) && numericAmount > 0;

  const preQuote = useMemo(
    () =>
      hasAmount
        ? buildSendPreQuote(
            originCountry,
            destinationCountry,
            numericAmount,
            amountAnchor
          )
        : null,
    [hasAmount, originCountry, destinationCountry, numericAmount, amountAnchor]
  );

  // Gallery mock of pre-quote latency.
  useEffect(() => {
    if (!hasAmount) {
      setPreQuoteLoading(false);
      return;
    }
    setPreQuoteLoading(true);
    const timer = window.setTimeout(() => setPreQuoteLoading(false), 350);
    return () => window.clearTimeout(timer);
  }, [
    hasAmount,
    numericAmount,
    amountAnchor,
    originCountry,
    destinationCountry,
    funding,
  ]);

  const sourceCurrency =
    preQuote?.summary.best?.sourceCurrency ?? baseSourceCurrency;

  const options = useMemo(() => preQuote?.options ?? [], [preQuote]);

  const availableFunding = useMemo(
    () => availableFundingMethods(options),
    [options]
  );

  const availablePayoutMethods = useMemo<PayoutMethodType[]>(() => {
    const scoped = funding
      ? options.filter((o) => o.fundingMethod === funding)
      : options;
    const seen = new Set<PayoutMethodType>();
    for (const option of scoped) {
      if (isSupportedPayoutMethod(option.payoutMethod)) {
        seen.add(option.payoutMethod);
      }
    }
    return SUPPORTED_PAYOUT_METHODS.filter((m) => seen.has(m));
  }, [options, funding]);

  const routeUnavailable =
    Boolean(preQuote) && hasAmount && availablePayoutMethods.length === 0;

  const handleSelectPayoutMethod = useCallback((method: PayoutMethodType) => {
    setPendingMethod(method);
    setPayoutStep("details");
  }, []);

  const cardBookEntries = useMemo(
    () =>
      compatibleBookDestinations(
        bookDestinations,
        PayoutMethodType.PUSH_TO_CARD,
        options
      ),
    [bookDestinations, options]
  );

  const bankBookEntries = useMemo(
    () =>
      compatibleBookDestinations(
        bookDestinations,
        PayoutMethodType.BANK_ACCOUNT,
        options
      ),
    [bookDestinations, options]
  );

  const pinBookDestination = useCallback(
    (method: PayoutMethodType, entry: CompatibleBookDestination) => {
      const option =
        options.find((o) => o.capabilityKey === entry.capabilityKey) ?? null;
      setSelection({
        method,
        option,
        payout: entry.payout,
      });
      setPopup(null);
    },
    [options, setSelection]
  );

  const handlePayoutPick = useCallback(
    (pick: FlatPayoutPick) => {
      if (pick.kind === "card") {
        pinBookDestination(PayoutMethodType.PUSH_TO_CARD, pick.entry);
        return;
      }
      if (pick.kind === "bank") {
        pinBookDestination(PayoutMethodType.BANK_ACCOUNT, pick.entry);
        return;
      }
      if (pick.kind === "add") {
        handleSelectPayoutMethod(PayoutMethodType.PUSH_TO_CARD);
        return;
      }
      if (pick.kind === "addBank") {
        handleSelectPayoutMethod(PayoutMethodType.BANK_ACCOUNT);
        return;
      }
      handleSelectPayoutMethod(pick.method);
    },
    [handleSelectPayoutMethod, pinBookDestination]
  );

  const handleDetailsSubmit = useCallback(
    (result: PayoutDetailsResult) => {
      if (!pendingMethod) return;
      setSelection({
        method: pendingMethod,
        option: result.option,
        payout: result.payout,
      });
      setPopup(null);
    },
    [pendingMethod, setSelection]
  );

  const payoutIcon = useMemo(() => {
    switch (selection?.method) {
      case PayoutMethodType.BANK_ACCOUNT:
        return <BankIcon size={20} />;
      case PayoutMethodType.CASH_PICKUP:
        return <CashSpecialIcon size={20} />;
      case PayoutMethodType.PUSH_TO_CARD:
        return <CardIcon size={20} />;
      default:
        return <WalletIcon size={20} />;
    }
  }, [selection]);

  const payoutSummary = useMemo(() => {
    if (!funding) return t("transfer.selectFundingFirst");
    if (!selection) return t("transfer.selectPayoutMethod");
    const method = t(`payout.methods.${selection.method}`);
    const { payout } = selection;
    switch (selection.method) {
      case PayoutMethodType.BANK_ACCOUNT: {
        const last4 =
          payout.last4 ||
          payout.accountNumber?.replace(/\D/g, "").slice(-4);
        const stored =
          payout.bankId && !looksLikePayerCode(payout.bankId)
            ? payout.bankId
            : null;
        const line = formatBankAccountLabel(stored, last4);
        if (line) return line;
        return last4 ? `${method} · •••• ${last4}` : method;
      }
      case PayoutMethodType.CASH_PICKUP:
        return payout.payerId ? `${method} · ${payout.payerId}` : method;
      case PayoutMethodType.PUSH_TO_CARD:
        // Card label parity: BRAND-0000.
        return payout.last4
          ? `${(payout.brand || "CARD").toUpperCase()}-${payout.last4}`
          : method;
      case PayoutMethodType.MOBILE_WALLET:
        return payout.phone ? `${method} · ${payout.phone}` : method;
      default:
        return method;
    }
  }, [funding, selection, t]);

  const fundingSummary = useMemo(() => {
    if (!funding) return t("transfer.selectFundingMethod");
    if (FUNDING_METHOD_CATEGORY[funding] === "CARD" && fundingInstrument) {
      // Card label parity: BRAND-0000.
      return `${(fundingInstrument.brand || "CARD").toUpperCase()}-${fundingInstrument.last4}`;
    }
    return t(`funding.methods.${funding}`, { defaultValue: funding });
  }, [funding, fundingInstrument, t]);

  const fundingIcon = useMemo(() => {
    if (!funding) return <WalletIcon size={20} />;
    const category = FUNDING_METHOD_CATEGORY[funding];
    if (category === "CARD" && fundingInstrument?.brand) {
      const brand = fundingInstrument.brand;
      const upper = brand.toUpperCase();
      const logo = upper.includes("VISA")
        ? visaLogo
        : upper.includes("MASTERCARD")
          ? masterCardLogo
          : null;
      if (logo) return <img src={logo} alt={`${brand} logo`} />;
    }
    if (category === "ACCOUNT") return <BankIcon size={20} />;
    if (category === "CASH") return <CashSpecialIcon size={20} />;
    return <WalletIcon size={20} />;
  }, [funding, fundingInstrument?.brand]);

  const recipientSummary = recipient
    ? recipient.fullName ||
      [recipient.firstName, recipient.lastName].filter(Boolean).join(" ")
    : t("transfer.selectRecipient");

  const selectedOption = useMemo<EstimateOption | null>(() => {
    if (!selection || !funding) return null;
    const scoped = options.filter((o) => o.payoutMethod === selection.method);
    return (
      optionForFundingMethod(scoped, funding) ?? selection.option ?? null
    );
  }, [options, selection, funding]);

  const selectedFundingDetails = funding ? sendFundingDetails[funding] : undefined;
  const amountBoundVerdict = compareSendToBound(
    hasAmount ? numericAmount : null,
    selectedFundingDetails
  );
  const amountOutOfRange =
    amountBoundVerdict === "below_min" || amountBoundVerdict === "above_max";

  const [confirmLoading, setConfirmLoading] = useState(false);

  // Sequential compose: CARD funding also needs an instrument.
  const fundingReady =
    funding != null &&
    (FUNDING_METHOD_CATEGORY[funding] !== "CARD" ||
      Boolean(fundingInstrument?.paymentMethodId));
  const payoutReady = Boolean(selection);
  const recipientReady = Boolean(recipient);

  const activeStep: ComposeStep | null = !hasAmount
    ? null
    : !fundingReady
      ? "funding"
      : !recipientReady
        ? "recipient"
        : !payoutReady
          ? "payout"
          : null;

  const isComposeActive = (step: ComposeStep) => activeStep === step;
  const isComposeReady = (step: ComposeStep) => {
    if (step === "funding") return fundingReady;
    if (step === "payout") return payoutReady;
    return recipientReady;
  };
  const isComposeLocked = (step: ComposeStep) => {
    if (!hasAmount) return true;
    if (activeStep === null) return false;
    if (isComposeReady(step)) return false;
    return (
      COMPOSE_STEP_ORDER.indexOf(step) > COMPOSE_STEP_ORDER.indexOf(activeStep)
    );
  };

  const railsReady = hasAmount && fundingReady && payoutReady;
  const composeReady = railsReady && recipientReady;
  const quoteMatches =
    Boolean(selectedOption?.capabilityKey) &&
    !preQuoteLoading &&
    !quoteLoading;
  const blockForPricing = railsReady && !quoteMatches;
  const ctaWaiting = confirmLoading || blockForPricing;
  const canConfirm = composeReady && quoteMatches && !routeUnavailable;
  const showRainbow =
    (canConfirm || confirmLoading) && !amountOutOfRange;

  const nudgeIncompleteCompose = useCallback(() => {
    setShake(true);
    window.setTimeout(() => setShake(false), 500);
    const scrollToGap = () => {
      const target =
        document.querySelector(".estimated-quote-section.shake") ??
        document.querySelector(".payout-container .payment-required") ??
        document.querySelector(".payout-container .shake") ??
        document.querySelector(".estimated-quote-section");
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    requestAnimationFrame(scrollToGap);
    window.setTimeout(scrollToGap, 50);
  }, []);

  const handleConfirm = useCallback(() => {
    if (confirmLoading) return;

    if (
      !hasAmount ||
      !funding ||
      !fundingReady ||
      !selection ||
      !payoutReady ||
      !recipient ||
      !recipientReady
    ) {
      nudgeIncompleteCompose();
      return;
    }

    if (routeUnavailable) return;

    if (amountOutOfRange) {
      setPopup("amountLimit");
      return;
    }

    // Compose complete — wait for mock pricing without shake.
    if (preQuoteLoading || !selectedOption?.capabilityKey || quoteLoading) {
      return;
    }

    setConfirmLoading(true);
    setQuoteLoading(true);
    const quote = buildMockQuote(numericAmount);
    setQuoteState(quote);
    setDraft({
      sourceCountry: originCountry,
      destinationCountry,
      sourceCurrency,
      destinationCurrency,
      amount: numericAmount,
      fundingMethod: funding,
      payoutMethod: selection.method,
      payout: selection.payout,
      recipient,
      capabilityKey: selectedOption.capabilityKey,
      fundingPaymentMethodId:
        FUNDING_METHOD_CATEGORY[funding] === "CARD"
          ? fundingInstrument?.paymentMethodId
          : undefined,
    });
    window.setTimeout(() => {
      setQuoteLoading(false);
      navigate(SEND_ROUTES.SENDING, { replace: true });
    }, 400);
  }, [
    confirmLoading,
    selection,
    funding,
    fundingReady,
    fundingInstrument,
    recipient,
    recipientReady,
    payoutReady,
    selectedOption,
    hasAmount,
    routeUnavailable,
    amountOutOfRange,
    preQuoteLoading,
    quoteLoading,
    nudgeIncompleteCompose,
    numericAmount,
    originCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    setQuoteState,
    setDraft,
    navigate,
  ]);

  const estimatedRate = preQuote?.summary.best?.customerFxRate;

  const pricingOption =
    selectedOption ?? (hasAmount ? (preQuote?.summary.best ?? null) : null);
  const fee = pricingOption?.customerFee ?? 0;
  const totalCost = pricingOption
    ? pricingOption.sendAmount + pricingOption.customerFee
    : null;

  const showFirstSendWarning = isFirstSendWarningVisible({
    sendAmount:
      amountAnchor === "send"
        ? hasAmount
          ? numericAmount
          : null
        : pricingOption?.sendAmount,
    sourceCurrency,
    fundingCategory:
      funding && fundingReady ? FUNDING_METHOD_CATEGORY[funding] : null,
  });

  const feeDisplay = useMemo(
    () => `$${amountFormatter(fee, sourceCurrency)}`,
    [fee, sourceCurrency]
  );

  const totalPaymentDisplay = useMemo(() => {
    const formatted =
      totalCost != null
        ? amountFormatter(totalCost, sourceCurrency)
        : hasAmount
          ? amountFormatter(numericAmount + fee, sourceCurrency)
          : amountFormatter(0, sourceCurrency);
    return `$${formatted}`;
  }, [totalCost, hasAmount, numericAmount, fee, sourceCurrency]);

  const receiveFormatted = useMemo(() => {
    const value =
      selectedOption?.receiveAmount ?? preQuote?.summary.best?.receiveAmount;
    if (value == null) return undefined;
    return amountFormatter(value, destinationCurrency, { country: "en-US" });
  }, [selectedOption, preQuote, destinationCurrency]);

  const sendFormatted = useMemo(() => {
    const value =
      selectedOption?.sendAmount ?? preQuote?.summary.best?.sendAmount;
    if (value == null) return undefined;
    return amountFormatter(value, sourceCurrency, { country: "en-US" });
  }, [selectedOption, preQuote, sourceCurrency]);

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="scrollable need-help-header"
      >
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => navigate(-1)}
            onIconClick={openSupport}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <section>
            <Title title={t("transfer.title")} align="left" color={TITLE_COLOR} />
            <section
              className={`estimated-quote-section stacked-quote${
                shake && !hasAmount ? " shake" : ""
              }`}
            >
              <section className="quote-container">
                <div className="banrural-amounts">
                  <div className="banrural-send-wrap">
                    <QuoteInfo
                      label={t("transfer.youSend")}
                      currency={sourceCurrency}
                      textColor={bodyCopy}
                      mode="input"
                      side="send"
                      isActive={amountAnchor === "send"}
                      derivedAmount={
                        amountAnchor === "receive" ? sendFormatted : undefined
                      }
                      showSelectorChevron
                      onSelect={() => setPopup("origin")}
                      icon={<FlagCircle iso2={originCountry.toLowerCase()} />}
                    />
                    <span
                      className="banrural-swap"
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowExcratePopup(true)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setShowExcratePopup(true);
                        }
                      }}
                      style={{ background: backgroundBtn }}
                    >
                      <ExchangeIcon size={18} colors={[textColorBtn, textColorBtn]} />
                    </span>
                  </div>
                  <QuoteInfo
                    label={t("transfer.youReceive")}
                    currency={destinationCurrency}
                    textColor={bodyCopy}
                    mode="input"
                    side="receive"
                    isActive={amountAnchor === "receive"}
                    derivedAmount={
                      amountAnchor === "send" ? receiveFormatted : undefined
                    }
                    hideCurrencySymbol={destinationCurrency !== "USD"}
                    showSelectorChevron
                    onSelect={() => setPopup("destination")}
                    icon={
                      <FlagCircle iso2={destinationCountry.toLowerCase()} />
                    }
                    bottomSlot={
                      <section
                        className="banrural-exchange-rate banrural-exchange-rate--alert"
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowExcratePopup(true)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setShowExcratePopup(true);
                          }
                        }}
                        style={
                          {
                            "--bgc": bannerHighlightBackground ?? bodyCopy,
                            "--colorc": solidStop(
                              bannerHighlightIconColors?.[0] ?? title,
                            ),
                            "--fillc": bannerHighlightColor ?? title,
                          } as CSSProperties
                        }
                      >
                        <span className="banrural-exchange-rate-icon">i</span>
                        <span className="banrural-exchange-rate-text">
                          <b>
                            {estimatedRate
                              ? t("exchange.rate", {
                                  from: sourceCurrency,
                                  rate: estimatedRate.toFixed(4),
                                  to: destinationCurrency,
                                })
                              : t("exchange.rate", {
                                  from: sourceCurrency,
                                  rate: "—",
                                  to: destinationCurrency,
                                })}
                          </b>
                          <label>{t("exchange.estimatedRate")}</label>
                        </span>
                      </section>
                    }
                  />
                </div>
              </section>
            </section>

            <section className="payout-container">
              <PayoutInfo
                title={t("transfer.payWith")}
                subtitle={fundingSummary}
                icon={fundingIcon}
                background={backgroundBtn}
                color={textColorBtn}
                locked={isComposeLocked("funding")}
                onChange={() => setPopup("funding")}
                className={`${isComposeActive("funding") ? "payment-required" : ""} ${
                  shake && hasAmount && !fundingReady ? "shake" : ""
                }`.trim()}
              />

              <PayoutInfo
                title={t("transfer.recipient")}
                subtitle={recipientSummary}
                icon={<WalletIcon size={20} />}
                background={backgroundBtn}
                color={textColorBtn}
                locked={isComposeLocked("recipient")}
                onChange={() => setPopup("recipient")}
                className={`${isComposeActive("recipient") ? "payment-required" : ""} ${
                  shake && hasAmount && !recipientReady ? "shake" : ""
                }`.trim()}
              />

              <PayoutInfo
                title={t("transfer.receiveIn")}
                subtitle={payoutSummary}
                icon={payoutIcon}
                background={backgroundBtn}
                color={textColorBtn}
                locked={isComposeLocked("payout")}
                onChange={() => {
                  setPayoutStep("method");
                  setPendingMethod(null);
                  setPopup("payout");
                }}
                className={`${isComposeActive("payout") ? "payment-required" : ""} ${
                  shake && hasAmount && !payoutReady ? "shake" : ""
                }`.trim()}
              />

              {showFirstSendWarning && firstSend ? (
                <FirstSendWarning copy={firstSend} />
              ) : null}

              <BodyCopy className="footer-text transfer-timing-note" textColor={bodyCopy}>
                <b>{t("transfer.instantTransfer")}</b>
                {" "}
                <span className="transfer-timing-note-rest">
                  {t("transfer.mayTakeFew")}
                </span>
              </BodyCopy>

              {routeUnavailable ? (
                <BodyCopy className="footer-text" textColor={bodyCopy}>
                  <b>{t("transfer.routeUnavailableTitle")}</b>
                  <br />
                  {t("transfer.routeUnavailableHint")}
                </BodyCopy>
              ) : null}
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <FooterDetailSummary
              heading={t("transfer.detail")}
              fee={t("transfer.fee", {
                amount: feeDisplay,
                currency: sourceCurrency,
              })}
              total={t("transfer.totalPayment", {
                amount: totalPaymentDisplay,
                currency: sourceCurrency,
              })}
              titleColor={TITLE_COLOR}
              textColor={TITLE_COLOR}
            />
            <div className={`button-wrapper ${showRainbow ? "rainbow-ready" : ""}`}>
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={ctaWaiting}
                onClick={handleConfirm}
              >
                {confirmLoading
                  ? t("transfer.prepare.loading")
                  : blockForPricing
                    ? t("transfer.loadingQuote")
                    : t("transfer.send")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>

      <Popup
        visible={popup === "amountLimit" && amountOutOfRange}
        onClose={() => setPopup(null)}
      >
        <AmountOutOfRangeInfo
          ns="send"
          kind={amountBoundVerdict === "below_min" ? "below_min" : "above_max"}
          method={t(`funding.methods.${funding ?? ""}`, {
            defaultValue: funding ?? "",
          })}
          bound={
            amountBoundVerdict === "below_min"
              ? (selectedFundingDetails?.minAmount ?? 0)
              : (selectedFundingDetails?.maxAmount ?? 0)
          }
          currency={sourceCurrency}
          onClose={() => setPopup(null)}
        />
      </Popup>

      <Popup visible={popup === "payout"} onClose={() => setPopup(null)}>
        {payoutStep === "method" ? (
          <SelectPayoutMethodSlot
            selectedMethod={selection?.method ?? null}
            selectedPaymentMethodId={
              selection?.method === PayoutMethodType.PUSH_TO_CARD ||
              selection?.method === PayoutMethodType.BANK_ACCOUNT
                ? selection.payout.paymentMethodId ?? null
                : null
            }
            available={availablePayoutMethods}
            cardEntries={cardBookEntries}
            bankEntries={bankBookEntries}
            loading={payoutPickerLoading}
            onPick={handlePayoutPick}
            onClose={() => setPopup(null)}
          />
        ) : (
          <PayoutDetailsSlot
            method={pendingMethod as PayoutMethodType}
            destinationCountry={destinationCountry}
            options={options}
            bookDestinations={
              savedListsUi === "empty" ? [] : bookDestinations
            }
            habitsBusy={savedListsUi === "loading" || habitsLoadDelay}
            value={
              selection && selection.method === pendingMethod
                ? selection.payout
                : null
            }
            onBack={() => setPayoutStep("method")}
            onClose={() => setPopup(null)}
            onSubmit={handleDetailsSubmit}
          />
        )}
      </Popup>

      <Popup visible={popup === "origin"} onClose={() => setPopup(null)}>
        <SelectOriginCountrySlot
          countries={originCountriesForPicker}
          selected={originCountry}
          onSelect={handleOriginChange}
          onClose={() => setPopup(null)}
        />
      </Popup>

      <Popup visible={popup === "destination"} onClose={() => setPopup(null)}>
        <SelectDestinationCountrySlot
          countries={destinationCountriesForPicker}
          selected={destinationCountry}
          onSelect={handleDestinationChange}
          onClose={() => setPopup(null)}
        />
      </Popup>

      <Popup visible={popup === "recipient"} onClose={() => setPopup(null)}>
        <RecipientDetailsSlot
          value={recipient}
          recentBusy={savedListsUi === "loading" || recentLoadDelay}
          recentEmpty={savedListsUi === "empty"}
          onClose={() => setPopup(null)}
          onSubmit={(r, destinations) => {
            setRecipient(r);
            // `undefined` keeps prior book habits; `[]` clears for free-text.
            if (destinations !== undefined) {
              setBookDestinations(destinations);
              // Keep payout selection unless it pins a saved instrument that
              // does not belong to the new recipient's book.
              const pinnedPmId = selection?.payout?.paymentMethodId;
              if (
                pinnedPmId &&
                !destinations.some((d) => d.paymentMethodId === pinnedPmId)
              ) {
                setSelection(null);
              }
            }
            setPopup(null);
          }}
        />
      </Popup>

      <Popup visible={popup === "funding"} onClose={() => setPopup(null)}>
        <SelectFundingMethodSlot
          selectedMethod={funding}
          selectedPaymentMethodId={fundingInstrument?.paymentMethodId ?? null}
          available={availableFunding}
          cards={sendSavedCards}
          loading={fundingPickerLoading}
          details={sendFundingDetails}
          onPick={handleFundingPick}
          onClose={() => setPopup(null)}
        />
      </Popup>

      <Popup
        visible={showExcratePopup}
        onClose={() => setShowExcratePopup(false)}
      >
        <section className="qoute-popup">
          <WarningIcon size={60} />
          <BodyCopy textColor={bodyCopy}>
            <Trans i18nKey="transfer.quote.disclaimer" ns="send" />
          </BodyCopy>
        </section>
      </Popup>
      {supportSheet}
    </>
  );
});
